import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
  LinearProgress,
  FormHelperText,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import useAuth from "../../context/AuthProvider";
import { apiClient } from "../../helper/api";
import Swal from "sweetalert2";
import { PrimaryBlueColor, DarkBlueBtnColor } from "../../helper/theme";

export interface NotificationPreferencesData {
  tagAlerts: boolean;
  signatureRequests: boolean;
  stampedDocuments: boolean;
  diligentSearch: boolean;
  showDiligentSearchToggle: boolean;
  /** When true, user is DS-only and should see only the "DS - New documents" toggle */
  showOnlyDiligentSearchToggle: boolean;
}

const defaultPreferences: NotificationPreferencesData = {
  tagAlerts: true,
  signatureRequests: true,
  stampedDocuments: true,
  diligentSearch: true,
  showDiligentSearchToggle: false,
  showOnlyDiligentSearchToggle: false,
};

interface NotificationPreferencesFormProps {
  onClose?: () => void;
}

export const NotificationPreferencesForm: React.FC<NotificationPreferencesFormProps> = ({ onClose }) => {
  const { user } = useAuth();
  const token = user?.token;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPreferencesData>(defaultPreferences);

  const fetchPreferences = () => {
    if (!token) return;
    setLoading(true);
    apiClient("/Users/notificationPreferences", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((res: any) => {
        if (res?.data) {
          const d = res.data;
          setPrefs({
            tagAlerts: !!d.tagAlerts,
            signatureRequests: !!d.signatureRequests,
            stampedDocuments: !!d.stampedDocuments,
            diligentSearch: !!d.diligentSearch,
            showDiligentSearchToggle: !!d.showDiligentSearchToggle,
            showOnlyDiligentSearchToggle: !!d.showOnlyDiligentSearchToggle,
          });
        }
      })
      .catch(() => {
        setPrefs({ ...defaultPreferences });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPreferences();
  }, [token]);

  const updatePref = <K extends keyof NotificationPreferencesData>(
    key: K,
    value: boolean
  ) => {
    if (key === "showDiligentSearchToggle") return;
    setPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const setAll = (on: boolean) => {
    setPrefs((prev) => ({
      ...prev,
      tagAlerts: on,
      signatureRequests: on,
      stampedDocuments: on,
      diligentSearch: on,
    }));
  };

  const allOn =
    prefs.tagAlerts &&
    prefs.signatureRequests &&
    prefs.stampedDocuments &&
    prefs.diligentSearch;

  const dsOnly = prefs.showOnlyDiligentSearchToggle;

  const handleSave = () => {
    if (!token) return;
    setSaving(true);
    apiClient("/Users/notificationPreferences", {
      method: "PUT",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify({
        tagAlerts: prefs.tagAlerts,
        signatureRequests: prefs.signatureRequests,
        stampedDocuments: prefs.stampedDocuments,
        diligentSearch: prefs.diligentSearch,
      }),
    })
      .then((res: any) => {
        if (res?.data !== undefined) {
          Swal.fire({
            icon: "success",
            title: "Saved",
            text: "Your email notification preferences have been saved.",
            confirmButtonText: "OK",
          });
          onClose?.();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res?.message || "Failed to save preferences.",
            confirmButtonText: "OK",
          });
        }
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to save preferences.",
          confirmButtonText: "OK",
        });
      })
      .finally(() => setSaving(false));
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: PrimaryBlueColor,
      "& + .MuiSwitch-track": { backgroundColor: DarkBlueBtnColor },
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      opacity: 0.5,
    },
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", py: 2 }}>
        <LinearProgress sx={{ "& .MuiLinearProgress-bar": { backgroundColor: PrimaryBlueColor } }} />
      </Box>
    );
  }

  const sectionTitleSx = {
    color: "text.secondary",
    fontWeight: 600,
    letterSpacing: "0.08em",
    display: "block",
    // mb: 1.5,
  };

  const paperSectionSx = {
    p: 2,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "grey.50",
    mb: 2,
  };

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", p: onClose ? 2 : 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <NotificationsActiveOutlinedIcon sx={{ fontSize: 28, color: "primary.main" }} />
        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: "0.02em", color: "text.primary" }}>
          Email Notifications
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary", lineHeight: 1.6 }}>
        Choose which email notifications you want to receive. In-portal inbox
        notifications (bell icon) are always on and are not affected by these
        settings.
      </Typography>

      {!dsOnly && (
        <>
          <Typography variant="overline" sx={sectionTitleSx}>
            Master
          </Typography>
          <Paper variant="outlined" sx={paperSectionSx}>
          <FormControlLabel
            control={
              <Switch
                checked={allOn}
                onChange={(_, checked) => setAll(checked)}
                sx={switchSx}
              />
            }
            label={allOn ? "All On" : "All Off"}
            sx={{ ml: 0 }}
          />
          </Paper>
        </>
      )}

      <Typography variant="overline" sx={sectionTitleSx}>
        {dsOnly ? "Email notifications" : "By notification type"}
      </Typography>
      <Paper variant="outlined" sx={paperSectionSx}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {!dsOnly && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={prefs.tagAlerts}
                    onChange={(_, checked) => updatePref("tagAlerts", checked)}
                    sx={switchSx}
                  />
                }
                label="Tag Alerts"
                sx={{ ml: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={prefs.signatureRequests}
                    onChange={(_, checked) =>
                      updatePref("signatureRequests", checked)
                    }
                    sx={switchSx}
                  />
                }
                label="Signature Requests"
                sx={{ ml: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={prefs.stampedDocuments}
                    onChange={(_, checked) =>
                      updatePref("stampedDocuments", checked)
                    }
                    sx={switchSx}
                  />
                }
                label="Stamped Documents"
                sx={{ ml: 0 }}
              />
            </>
          )}
          {prefs.showDiligentSearchToggle && (
            <FormControlLabel
              control={
                <Switch
                  checked={prefs.diligentSearch}
                  onChange={(_, checked) =>
                    updatePref("diligentSearch", checked)
                  }
                  sx={switchSx}
                />
              }
              label="Diligent Search — New documents"
              sx={{ ml: 0 }}
            />
          )}
        </Box>
      </Paper>

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
          // justifyContent: "flex-end",
          mt: 3,
          // mx: -2,
          // pt: 2,
          // borderTop: 1,
          // borderColor: "divider",
          // backgroundColor: "grey.50",
          // borderRadius: 2,
          // mb: -2,
          // px: 2,
          // py: 2,
        }}
      >
        {onClose && (
          <Button
            type="button"
            variant="outlined"
            startIcon={<CloseOutlinedIcon />}
            onClick={onClose}
            sx={{ fontWeight: 600, textTransform: "none" }}
          >
            Close
          </Button>
        )}
        <Button
          type="button"
          variant="contained"
          startIcon={saving ? null : <SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "#2099d2",
            "&:hover": { backgroundColor: "#1a7ba8" },
          }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
};
